import { CommonModule } from '@angular/common';
import { Component, effect, OnDestroy, OnInit } from '@angular/core';
import { TableModule } from 'primeng/table';
import { TableComponent } from '../../../../core/components/table/table.component';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TableReadOnlyComponent } from '../../../../core/components/table-read-only/table-read-only.component';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { HttpErrorResponse } from '@angular/common/http';
import { ReactiveFormsModule, FormsModule, FormBuilder } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { GenericErrorResponse } from '../../../../core/models/generic-response.model';
import { InlineResponse } from '../../../../core/models/inline-response.model';
import { StoreService } from '../../../../core/services/store.service';
import { Gamme, Otp } from './outil-achat.model';
import { ReferentialsOutilAchatService } from './referentials-outil-achat.service';
import { AddOtpComponent } from './add-otp/add-otp.component';
import { AddGammeComponent } from './add-gamme/add-gamme.component';

const dialogConfig = {
  modal: true,
  width: '30vw',
  height: 'auto',
  maximizable: true,
  breakpoints: {
    '1199px': '60vw', // Largeur à 60% du viewport si la fenêtre fait moins de 1199px de large
    '991px': '70vw', // Largeur à 70% du viewport si la fenêtre fait moins de 991px
    '575px': '90vw', // Largeur à 90% du viewport si la fenêtre fait moins de 575px
  },
};

@Component({
  selector: 'app-referentials-otp',
  standalone: true,
  imports: [CommonModule, TableModule, TableComponent, TableReadOnlyComponent, ButtonModule, InputTextModule, CheckboxModule, ReactiveFormsModule, FormsModule, ToastModule, DropdownModule, TooltipModule, TranslocoModule],
  providers: [DialogService, MessageService, provideTranslocoScope({ scope: 'referentiels' })],
  templateUrl: './referentials-outil-achat.component.html',
  styleUrl: './referentials-outil-achat.component.css'
})
export class ReferentialsOutilAchatComponent implements OnInit, OnDestroy {
 private destroy$: Subject<boolean> = new Subject<boolean>();

  userHasReadWritePermission: boolean = false;

  ref: DynamicDialogRef | undefined;

  otpResult!: Otp[];
  selectedOtp?: Otp;
  totalRecords: number = 0;

  disableSubmit = false;
  errors: GenericErrorResponse[] = [];
  loading: boolean = false;

  editRowIndex: number | null = null; // Variable pour garder la ligne en cours d'édition
  editingLine: any = null;
  clonedOtp: { [s: string]: Otp } = {};


  otpForm = this.formBuilder.group({
    id: this.formBuilder.control<number | null>(null),
    eotp: [''],
    designation: [''],
  });

  gammeResult!: Gamme[];
  selectedGamme?: Gamme;
  totalGammeRecords: number = 0;

  editGammeRowIndex: number | null = null; // Variable pour garder la ligne en cours d'édition
  editingGammeLine: any = null;
  clonedGamme: { [s: string]: Gamme } = {};


  gammeForm = this.formBuilder.group({
    id: this.formBuilder.control<number | null>(null),
    designation: [''],
  });

  constructor(
    private outilAchatService: ReferentialsOutilAchatService,
    private storeService: StoreService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private formBuilder: FormBuilder,
    private translocoService: TranslocoService
  ) {
    effect(() => {
      const selectedContrat = this.storeService.selectedContract();
      this.ngOnInit();
    });
  }

  ngOnInit(): void {
    this.userHasReadWritePermission = this.storeService.hasAuthority('PARAMETRES_REFERENTIEL_RW');
    this.loadOtp();
    this.loadGamme();
  }

  private loadOtp() {
    this.loading = true;
    this.outilAchatService
      .getOptData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.otpResult = data;
        this.totalRecords = this.otpResult.length;
        this.loading = false;
      });
  }

  ajouterOtp() {
    this.ref = this.dialogService.open(AddOtpComponent, {
      ...dialogConfig,
      header: this.translocoService.translate('jgrid.nav.addtitle'),
    });
    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.showSuccessMessage('La ligne a été ajouté avec succès.');
        this.loadOtp();
      }
    });
  }

  modifierOtp(otp: Otp, index: number) {
    this.editRowIndex = index;
    this.editingLine = otp;
    this.selectedOtp = otp;
    const id = otp.id;
    this.otpForm.controls.id.patchValue(otp.id);
    this.otpForm.controls.designation.patchValue(otp.designation);
    this.otpForm.controls.eotp.patchValue(otp.eotp);
    this.clonedOtp[id.toString()] = { ...otp };
  }

  validerModifsOtp(otp: Otp) {
    this.outilAchatService.updateOtpData(this.otpForm.value as Otp).subscribe({
      next: (response) => {
        this.handleInlineResponse(response);
      },
      error: (error) => {
        this.handleHttpErrorResponse(error);
      },
    });
  }

  annulerModifsOtp(otp: Otp, index: number) {
    this.editRowIndex = null;
    this.editingLine = null;
    const id = otp.id;
    this.otpResult
      .filter((c) => c.id === id)
      .map((otp) => {
        otp = this.clonedOtp[id.toString()];
      });
    delete this.clonedOtp[id.toString()];
  }

  onRowSelect(otp: Otp) {
    this.selectedOtp = otp;
  }
  
  //GAMME 

  private loadGamme() {
    this.loading = true;
    this.outilAchatService
      .getGammeData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.gammeResult = data;
        this.totalGammeRecords = this.gammeResult.length;
        this.loading = false;
      });
  }

  ajouterGamme() {
    this.ref = this.dialogService.open(AddGammeComponent, {
      ...dialogConfig,
      header: this.translocoService.translate('jgrid.nav.addtitle'),
    });
    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.showSuccessMessage('La ligne a été ajouté avec succès.');
        this.loadGamme();
      }
    });
  }

  modifierGamme(gamme: Gamme, index: number) {
    this.editGammeRowIndex = index;
    this.editingGammeLine = gamme;
    this.selectedGamme = gamme;
    const id = gamme.id;
    this.gammeForm.controls.id.patchValue(gamme.id);
    this.gammeForm.controls.designation.patchValue(gamme.designation);
    this.clonedGamme[id.toString()] = { ...gamme };
  }

  validerModifsGamme() {
    this.outilAchatService.updateGammeData(this.gammeForm.value as Gamme).subscribe({
      next: (response) => {
        this.handleInlineResponse(response);
      },
      error: (error) => {
        this.handleHttpErrorResponse(error);
      },
    });
  }

  annulerModifsGamme(gamme: Gamme, index: number) {
    this.editGammeRowIndex = null;
    this.editingGammeLine = null;
    const id = gamme.id;
    this.gammeResult
      .filter((c) => c.id === id)
      .map((gam) => {
        gam = this.clonedGamme[id.toString()];
      });
    delete this.clonedGamme[id.toString()];
  }

  onRowGammeSelect(gam: Gamme) {
    this.selectedGamme = gam;
  }
  //COMMON
  private handleInlineResponse(response: InlineResponse): void {
    this.disableSubmit = false;
    if (response.success) {
      this.showSuccessMessage('La ligne a été modifiée avec succès.');
      this.loadOtp();
      this.loadGamme();
    } else {
      this.errors = response.errors;
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('error.popup.titre.defaut'),
        detail: 'Une erreur est survenue : ' + this.errors.map((e) => e.errorMsg).join(', '),
        life: 3000,
      });
    }
  }

  private handleHttpErrorResponse(error: HttpErrorResponse) {
    this.disableSubmit = false;
    this.errors = [];
    this.errors.push({
      errorInputId: error.status.toString(),
      errorCode: error.status.toString(),
      errorMsg: error.statusText,
    } as GenericErrorResponse);
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
  }

  showInfoMessage(infoMessage: string) {
    this.messageService.add({ severity: 'info', summary: this.translocoService.translate('info.popup.titre.defaut'), detail: infoMessage, life: 3000 });
  }

  showSuccessMessage(successMessage: string) {
    this.messageService.add({ severity: 'success', summary: this.translocoService.translate('summary.success'), detail: successMessage, life: 3000 });
  }

  showErrorMessage(errorMessage: string) {
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: errorMessage, life: 3000 });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
